import React, { useState } from "react"
import {
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import {
  CreateShipment,
  UpdateShipment,
  DeleteShipment,
} from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import { displayMessage } from "helpers/misc_helpers"
import moment from "moment"

const CPSShipmentsTable = ({ entry, myUser, shipments, reload }) => {
  const setLoading = useLoading()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [showNewShipmentModal, setShowNewShipmentModal] = useState(false)
  const [showConfirmDeliveryModal, setShowConfirmDeliveryModal] =
    useState(false)
  const [selectedShipment, setSelectedShipment] = useState(null)
  const [newShipment, setNewShipment] = useState({
    quantity: "",
    shippingDate: "",
    trackingNumber: "",
    shippingService: "",
    notes: "",
  })
  const [confirmDeliveryData, setConfirmDeliveryData] = useState({
    notes: "",
    arrivedQuantity: "",
  })
  const [error, setError] = useState(null)

  // Demo shipments data
  // Transform API shipments data to match frontend expectations
  const transformedShipments = shipments.map(shipment => ({
    ...shipment,
    status: shipment.delivered ? "Delivered" : "In Transit",
    dateCreated: shipment.dateCreated
      ? new Date(shipment.dateCreated).toLocaleDateString()
      : "",
  }))

  const handleCreateNewShipment = async () => {
    if (
      newShipment.quantity &&
      newShipment.shippingDate &&
      newShipment.trackingNumber
    ) {
      setLoading(true)
      setIsTableLoading(true)
      try {
        const shipmentData = {
          cpsRequestID: entry.id,
          quantity: parseInt(newShipment.quantity),
          shippingDate: newShipment.shippingDate,
          trackingNumber: newShipment.trackingNumber,
          shippingService: newShipment.shippingService || "",
          notes: newShipment.notes || "",
          delivered: false,
        }

        await CreateShipment(shipmentData)

        setShowNewShipmentModal(false)
        setNewShipment({
          quantity: "",
          shippingDate: "",
          trackingNumber: "",
          shippingService: "",
          notes: "",
        })

        // Reload data to show new shipment
        if (reload) {
          await reload()
        }
      } catch (error) {
        console.error("Error creating shipment:", error)
        displayMessage("Error creating shipment. Please try again.", setError)
      } finally {
        setLoading(false)
        setIsTableLoading(false)
      }
    } else {
      alert("Please fill in all required fields")
    }
  }

  const handleConfirmDelivery = shipment => {
    setSelectedShipment(shipment)
    setConfirmDeliveryData({
      notes: "",
      arrivedQuantity: shipment.quantity || "",
    })
    setShowConfirmDeliveryModal(true)
  }

  const handleSubmitConfirmDelivery = async () => {
    if (!selectedShipment) return

    const arrivedQty = parseInt(confirmDeliveryData.arrivedQuantity, 10)
    if (!arrivedQty || arrivedQty <= 0) {
      displayMessage("Please enter a valid arrived quantity", setError)
      return
    }

    setLoading(true)
    setIsTableLoading(true)
    try {
      const updateData = {
        id: selectedShipment.id,
        quantity: arrivedQty,
        shippingDate: selectedShipment.shippingDate,
        trackingNumber: selectedShipment.trackingNumber,
        notes: selectedShipment.notes || "",
        delivered: true,
        deliveredDate: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
        shippingService: selectedShipment.shippingService || "DHL Express",
      }

      await UpdateShipment(updateData)

      setShowConfirmDeliveryModal(false)
      setSelectedShipment(null)
      setConfirmDeliveryData({
        notes: "",
        arrivedQuantity: "",
      })

      // Reload data to show updated shipment
      if (reload) {
        await reload()
      }
    } catch (error) {
      console.error("Error confirming delivery:", error)
      displayMessage("Error confirming delivery. Please try again.", setError)
    } finally {
      setLoading(false)
      setIsTableLoading(false)
    }
  }

  const getStatusColor = status => {
    switch (status) {
      case "Delivered":
        return "success"
      case "In Transit":
        return "warning"
      case "Shipped":
        return "info"
      default:
        return "secondary"
    }
  }

  const getStatusIcon = status => {
    switch (status) {
      case "Delivered":
        return "bx-check-circle"
      case "In Transit":
        return "bx-truck"
      case "Shipped":
        return "bx-package"
      default:
        return "bx-time"
    }
  }

  return (
    <>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Raw Material Shipments</h5>
            <div className="d-flex align-items-center gap-2">
              <Badge color="primary" size="sm">
                {transformedShipments.length} Shipments
              </Badge>
              <Button
                color="success"
                size="sm"
                onClick={() => setShowNewShipmentModal(true)}
                title="Create New Shipment"
                disabled={isTableLoading}
              >
                <i className="bx bx-plus me-1"></i>
                New Shipment
              </Button>
            </div>
          </div>

          {isTableLoading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2 text-muted">Updating shipments...</div>
            </div>
          )}

          <div
            className="table-responsive"
            style={{ opacity: isTableLoading ? 0.5 : 1 }}
          >
            <Table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Quantity</th>
                  <th>Tracking</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transformedShipments.map(shipment => (
                  <tr key={shipment.id}>
                    <td>
                      <div>
                        <strong className="text-muted">
                          {moment(shipment.shippingDate).format("DD.MM.YYYY")}
                        </strong>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>
                          {shipment.quantity.toLocaleString()} pcs
                        </strong>
                      </div>
                    </td>
                    <td>
                      <span className="text-primary">
                        <i className="bx bx-package me-1"></i>
                        {shipment.trackingNumber}
                      </span>
                    </td>
                    <td>
                      <Badge color={getStatusColor(shipment.status)}>
                        <i
                          className={`bx ${getStatusIcon(
                            shipment.status
                          )} me-1`}
                        ></i>
                        {shipment.status}
                      </Badge>
                    </td>
                    <td style={{ width: "50px" }}>
                      {shipment.status === "In Transit" &&
                        myUser?.company?.id === entry?.cpsLabID && (
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleConfirmDelivery(shipment)}
                            title="Confirm Delivery"
                            disabled={isTableLoading}
                          >
                            <i className="bx bx-check"></i>
                          </Button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* New Shipment Modal */}
      <Modal
        isOpen={showNewShipmentModal}
        toggle={() => setShowNewShipmentModal(false)}
      >
        <ModalHeader toggle={() => setShowNewShipmentModal(false)}>
          Create New Shipment
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                value={newShipment.quantity}
                onChange={e =>
                  setNewShipment({ ...newShipment, quantity: e.target.value })
                }
                placeholder="Enter quantity"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="shippingDate">Shipping Date *</Label>
              <Input
                id="shippingDate"
                type="date"
                value={newShipment.shippingDate}
                onChange={e =>
                  setNewShipment({
                    ...newShipment,
                    shippingDate: e.target.value,
                  })
                }
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="trackingNumber">Tracking Number *</Label>
              <Input
                id="trackingNumber"
                type="text"
                value={newShipment.trackingNumber}
                onChange={e =>
                  setNewShipment({
                    ...newShipment,
                    trackingNumber: e.target.value,
                  })
                }
                placeholder="Enter tracking number"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="shippingService">Shipping Service</Label>
              <Input
                id="shippingService"
                type="text"
                value={newShipment.shippingService}
                onChange={e =>
                  setNewShipment({
                    ...newShipment,
                    shippingService: e.target.value,
                  })
                }
                placeholder="e.g., DHL, FedEx, UPS"
              />
            </FormGroup>
            <FormGroup>
              <Label for="notes">Notes</Label>
              <Input
                id="notes"
                type="textarea"
                value={newShipment.notes}
                onChange={e =>
                  setNewShipment({ ...newShipment, notes: e.target.value })
                }
                placeholder="Optional notes for this shipment"
                rows="3"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowNewShipmentModal(false)}
          >
            Cancel
          </Button>
          <Button color="success" onClick={handleCreateNewShipment}>
            Create Shipment
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirm Delivery Modal */}
      <Modal
        isOpen={showConfirmDeliveryModal}
        toggle={() => setShowConfirmDeliveryModal(false)}
      >
        <ModalHeader toggle={() => setShowConfirmDeliveryModal(false)}>
          Confirm Delivery - Shipment #{selectedShipment?.id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="arrivedQuantity">Arrived Quantity *</Label>
              <Input
                id="arrivedQuantity"
                type="number"
                value={confirmDeliveryData.arrivedQuantity}
                onChange={e =>
                  setConfirmDeliveryData({
                    ...confirmDeliveryData,
                    arrivedQuantity: e.target.value,
                  })
                }
                placeholder="Enter actually arrived quantity for this shipment"
                min="1"
              />
            </FormGroup>
            <FormGroup>
              <Label for="deliveryNotes">Delivery Notes</Label>
              <Input
                id="deliveryNotes"
                type="textarea"
                value={confirmDeliveryData.notes}
                onChange={e =>
                  setConfirmDeliveryData({
                    ...confirmDeliveryData,
                    notes: e.target.value,
                  })
                }
                placeholder="Optional notes about the delivery"
                rows="3"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowConfirmDeliveryModal(false)}
          >
            Cancel
          </Button>
          <Button color="success" onClick={handleSubmitConfirmDelivery}>
            Confirm Delivery
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CPSShipmentsTable
